import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import api, { onUnauthorized, ApiError } from '@/lib/api';
import type { User } from '@/lib/types';

type AuthStatus = 'loading' | 'authed' | 'anon';

interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (input: {
    email: string;
    password: string;
    username: string;
    displayName?: string;
    inviteCode: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (input: { email: string; password: string; inviteCode: string }) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get<{ user: User }>('/auth/me');
      setUser(res.user);
      setStatus('authed');
    } catch {
      setUser(null);
      setStatus('anon');
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    onUnauthorized(() => {
      setUser(null);
      setStatus('anon');
    });
    return () => onUnauthorized(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ user: User }>('/auth/login', { email, password });
    setUser(res.user);
    setStatus('authed');
  }, []);

  const signup = useCallback(
    async (input: {
      email: string;
      password: string;
      username: string;
      displayName?: string;
      inviteCode: string;
    }) => {
      const res = await api.post<{ user: User }>('/auth/signup', input);
      setUser(res.user);
      setStatus('authed');
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setUser(null);
      setStatus('anon');
    }
  }, []);

  const resetPassword = useCallback(
    async (input: { email: string; password: string; inviteCode: string }) => {
      const res = await api.post<{ user: User }>('/auth/reset-password', input);
      setUser(res.user);
      setStatus('authed');
    },
    [],
  );

  return (
    <AuthContext.Provider value={{ status, user, login, signup, logout, resetPassword, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.message);
      if (parsed.error) return parsed.error;
    } catch {
      // fall through
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}
